import events from "add-event-listener";
import * as React from "react";

import { themed } from "../../Internals/ThemeProvider/ThemedWorkaround";
import { nullElement, pullContext } from "../../TypingUtils";
import {
    UnitLayoutingContext,
    unitLayoutingContextTypes,
} from "../../FormParts/Unit/Internals/UnitBorderContext/UnitLayoutingContext";
import { areArraysNotEqual } from "../../../../../Common/ArrayUtils";

import { Colgroup } from "./Colgroup";
import * as styles from "./StickyTableHeader.styles";
import { HeaderTable } from "./StickyTableHeader.styles";
import { StickyTableHeaderBody } from "./StickyTableHeaderBody";

const SHADOW = 8;
const HEADER_TOP_PADDING = 10;

export interface HeaderProps {
    children: React.ReactNode;
    className?: string;
    columnWidth: number[];
    horizontalScroll: number;
    isFixed: boolean;
    left: number;
    paddingHeaderBottom: number;
    table: null | HTMLElement;
    verticalLines: number[];
    width: number;
}

type StickyHeaderPosition = "top" | "fixed" | "bottom";

interface State {
    headerPosition: StickyHeaderPosition;
    isSmallHeader: boolean;
}

export class StickyTableHeader extends React.Component<HeaderProps, State> {
    private readonly container = React.createRef<HTMLDivElement>();
    private readonly headerCaption = React.createRef<HTMLDivElement>();
    private readonly header = React.createRef<HTMLTableElement>();
    private readonly smallHeader = React.createRef<HTMLTableElement>();

    public static defaultProps = {
        headerPosition: "top",
        paddingHeaderBottom: 40,
        horizontalScroll: 0,
        width: 1000,
    };

    public static contextTypes = unitLayoutingContextTypes;

    public state: State = { headerPosition: "top", isSmallHeader: false };

    public componentDidMount(): void {
        if (!this.props.isFixed) {
            events.addEventListener(window, "scroll", this.handleRecalc);
            events.addEventListener(window, "resize", this.handleRecalc);
        }
    }

    public shouldComponentUpdate(nextProps: HeaderProps, nextState: State): boolean {
        return (
            this.props.width !== nextProps.width ||
            this.props.left !== nextProps.left ||
            this.props.horizontalScroll !== nextProps.horizontalScroll ||
            this.state.headerPosition !== nextState.headerPosition ||
            this.state.isSmallHeader !== nextState.isSmallHeader ||
            areArraysNotEqual(this.props.columnWidth, nextProps.columnWidth)
        );
    }

    public componentWillUnmount(): void {
        if (!this.props.isFixed) {
            events.removeEventListener(window, "scroll", this.handleRecalc);
            events.removeEventListener(window, "resize", this.handleRecalc);
        }
    }

    public render(): JSX.Element {
        const rowsStyle = {
            paddingLeft: this.getContext().paddingLeft,
            paddingRight: this.getContext().paddingRight,
            marginLeft: -this.props.horizontalScroll,
        };

        const headerContentStyle: React.CSSProperties = {};
        const headerContainerStyle: React.CSSProperties = {};
        const containerStyle: React.CSSProperties = {};
        const bottomLineStyle: React.CSSProperties = {};
        const bottomLineClass =
            this.state.headerPosition === "top" ? styles.headerBottomLine : styles.headerNotBottomLine;

        if (
            this.header.current != undefined &&
            this.smallHeader.current != undefined &&
            this.headerCaption.current != undefined
        ) {
            const headerHeight =
                this.header.current.offsetHeight +
                this.smallHeader.current.offsetHeight +
                this.headerCaption.current.offsetHeight;

            containerStyle.height = headerHeight;
            headerContentStyle.height = headerHeight + SHADOW;
        }
        switch (this.state.headerPosition) {
            case "top":
                bottomLineStyle.width = `${this.props.columnWidth.reduce((a, b) => a + b)}px`;
                break;
            case "bottom":
                headerContentStyle.width = this.props.width;
                headerContentStyle.bottom = this.props.paddingHeaderBottom + 2;
                break;
            case "fixed":
                headerContentStyle.width = this.props.width;
                if (this.props.left) {
                    headerContainerStyle.left = this.props.left;
                } else {
                    headerContainerStyle.left = "";
                }

                headerContainerStyle.width = this.props.width;
                headerContainerStyle.top = this.state.isSmallHeader
                    ? -(this.header.current?.offsetHeight ?? 0) - HEADER_TOP_PADDING
                    : 0;
                break;
            default:
                break;
        }

        const { captionRow, rows, smallRow } = this.getChildren();

        return (
            <div ref={this.container} style={containerStyle}>
                <div
                    css={themed(
                        this.state.headerPosition === "fixed" && styles.headerContainerFixed,
                        this.state.headerPosition === "bottom" && styles.headerContainerBottom
                    )}
                    style={headerContainerStyle}>
                    <div style={headerContentStyle}>
                        <div
                            css={styles.headerCaption}
                            ref={this.headerCaption}
                            style={{ paddingLeft: this.getContext().paddingLeft }}>
                            {captionRow}
                        </div>
                        <div
                            css={themed(
                                styles.headerRowsContainer,
                                this.state.headerPosition !== "top" && styles.headerRowsContainerSticky
                            )}
                            style={rowsStyle}>
                            <HeaderTable className={this.props.className} ref={this.header}>
                                <Colgroup
                                    columnWidth={this.props.columnWidth}
                                    verticalLines={this.props.verticalLines}
                                />
                                {rows}
                            </HeaderTable>
                            <HeaderTable className={this.props.className} ref={this.smallHeader}>
                                <Colgroup
                                    columnWidth={this.props.columnWidth}
                                    verticalLines={this.props.verticalLines}
                                />
                                {smallRow}
                            </HeaderTable>
                            <div css={bottomLineClass} style={bottomLineStyle} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public getHeight(): number {
        if (this.state.isSmallHeader) {
            if (this.smallHeader.current == undefined) {
                throw new Error("InvalidProgramState");
            }
            return this.smallHeader.current.offsetHeight;
        } else {
            if (this.container.current == undefined) {
                throw new Error("InvalidProgramState");
            }
            return this.container.current.offsetHeight;
        }
    }

    private getContext(): UnitLayoutingContext {
        return pullContext<UnitLayoutingContext>(this);
    }

    private readonly changeHeaderPosition = () => {
        if (this.props.table == undefined || this.container.current == undefined) {
            return;
        }

        const tableRect = this.props.table.getBoundingClientRect();
        const headerHeight = this.container.current.offsetHeight + HEADER_TOP_PADDING;
        const tableBottom = tableRect.bottom - this.props.paddingHeaderBottom;

        let headerPosition: StickyHeaderPosition = "bottom";
        const isSmallHeader = this.isSmallViewPort();

        if (isSmallHeader) {
            if (
                this.smallHeader.current == undefined ||
                this.header.current == undefined ||
                this.headerCaption.current == undefined
            ) {
                return;
            }
            const headerTop = tableRect.top;
            const smallHeaderHeight = this.smallHeader.current.offsetHeight;
            const headerBodyHeight = this.header.current.offsetHeight + this.headerCaption.current.offsetHeight;

            if (headerTop <= -headerBodyHeight && smallHeaderHeight < tableBottom) {
                headerPosition = "fixed";
            } else if (headerTop > -headerBodyHeight) {
                headerPosition = "top";
            }

            if (this.state.headerPosition !== headerPosition || this.state.isSmallHeader !== isSmallHeader) {
                this.setState({ headerPosition: headerPosition, isSmallHeader: isSmallHeader });
            }

            return;
        }

        if (tableRect.top <= 0 && headerHeight < tableBottom) {
            headerPosition = "fixed";
        } else if (tableRect.top > 0) {
            headerPosition = "top";
        }

        if (this.state.headerPosition !== headerPosition || this.state.isSmallHeader !== isSmallHeader) {
            this.setState({ headerPosition: headerPosition, isSmallHeader: isSmallHeader });
        }
    };

    private isSmallViewPort(): boolean {
        if (this.container.current == undefined) {
            return false;
        }
        const screenHeight = document.documentElement.offsetHeight;
        const headerHeight = this.container.current.offsetHeight;

        return screenHeight / headerHeight < 3;
    }

    private getChildren(): {
        captionRow: React.ReactNode | null;
        rows: React.ReactNode | null;
        smallRow: React.ReactNode | null;
    } {
        let captionRow: React.ReactNode | null = nullElement;
        let rows: React.ReactNode | null = nullElement;
        let smallRow: React.ReactNode | null = nullElement;

        React.Children.forEach(this.props.children ?? [], child => {
            if (React.isValidElement<{ type?: unknown }>(child) && child.type === StickyTableHeaderBody) {
                if (child.props.type === "smallHeader") {
                    smallRow = child;
                } else {
                    rows = child;
                }
            } else {
                captionRow = child;
            }
        });

        return { captionRow: captionRow, rows: rows, smallRow: smallRow };
    }

    private readonly handleRecalc = () => {
        if (this.props.table == undefined || this.container.current == undefined) {
            return;
        }

        const rect = this.props.table.getBoundingClientRect();
        const heightScreen = document.documentElement.offsetHeight;

        if ((rect.top <= heightScreen && rect.bottom >= 0) || this.state.headerPosition === "fixed") {
            this.changeHeaderPosition();
        }
    };
}
